'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-account.scss';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { SupporterIdentityFallback } from '../supporter-identity/supporter-identity-fallback.tsx';

/**
 * Supporter resolution pulls `supporter-sdk` (query hooks, generated schema, and
 * the mock fixtures `constants.ts`/`queries.ts` still statically import) into
 * whatever chunk references it. Only authenticated users ever need it, so it
 * loads on demand instead of riding along in the header chunk that every
 * anonymous visitor downloads.
 */
const SupporterIdentity = lazy(() => import('../supporter-identity/supporter-identity.tsx').then(m => ({
  default: m.SupporterIdentity
})));

/**
 * Avatar slot for the account nav. Renders the standard user icon in the shell
 * at the resolved component's exact dimensions, then swaps in the supporter's
 * initials once the chunk lands.
 */
const AccountAvatar: React.FC<{
  variant: 'minimal' | 'dropdown';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = t0 => {
  const $ = _c(4);
  const {
    variant,
    size,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== size || $[2] !== variant) {
    t1 = <Suspense fallback={<SupporterIdentityFallback variant={variant} size={size} className={className} />}><SupporterIdentity variant={variant} size={size} className={className} /></Suspense>;
    $[0] = className;
    $[1] = size;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
type NavAccountProps = {
  userMenu?: React.ReactNode;
  urlLogin?: string;
  urlAccountHome?: string;
  onClose?: () => void;
  /**
   * For apps where middleware guarantees every user is authenticated (my),
   * render the account UI optimistically while `/verify` is still in flight
   * instead of flashing a login button the user can never need.
   */
  assumeAuthenticated?: boolean;
  /** Render only a trigger button (used inside hamburger menu) */
  asTriggerOnly?: boolean;
  /** Called when trigger button is clicked (with asTriggerOnly) */
  onOpen?: () => void;
  /** Ref forwarded to the mobile takeover trigger for focus restoration */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
};

// These hardcoded links will be used if no user menu is supplied from Contentful.
export const FallbackUserMenu = t0 => {
  const $ = _c(25);
  const {
    itemWrapper: t1
  } = t0;
  const itemWrapper = t1 === undefined ? DropdownMenuItem : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["priority", "core"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getPublicConfig();
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    NEXT_PUBLIC_BASE_URL_MYACCOUNT
  } = t3;
  const {
    actions: authActions
  } = useAuth("actions");
  const Wrapper = itemWrapper;
  let t4;
  if ($[2] !== authActions || $[3] !== t) {
    t4 = async () => {
      trackClick(t("priority:nav.logOut"));
      authActions.logOut();
    };
    $[2] = authActions;
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const logOut = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArkIcon iconName="home-03" className="NavAccount__icon--small" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = t("core:actions.goToMyAccount");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">{t5}<LinkStyle noUnderline={true} noPadding={true} purpose="neutral"><Link href={NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#"} className="NavAccount__menu-item w-full py-comfy-xs px-comfy-sm">{t6}</Link></LinkStyle></span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== Wrapper || $[11] !== t7) {
    t8 = <Wrapper className="flex w-full align-start">{t7}</Wrapper>;
    $[10] = Wrapper;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArkIcon iconName="log-out-01" className="NavAccount__icon--small" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t("priority:nav.logOut");
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== logOut || $[17] !== t10) {
    t11 = <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">{t9}<LinkStyle noUnderline={true} noPadding={true} purpose="negative"><button type="button" onClick={logOut}>{t10}</button></LinkStyle></span>;
    $[16] = logOut;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== Wrapper || $[20] !== t11) {
    t12 = <Wrapper className="flex w-full align-start">{t11}</Wrapper>;
    $[19] = Wrapper;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t8) {
    t13 = <>{t8}{t12}</>;
    $[22] = t12;
    $[23] = t8;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};
export const NavAccount: React.FC<NavAccountProps> = t0 => {
  const $ = _c(76);
  const {
    userMenu,
    urlLogin,
    urlAccountHome,
    onClose,
    assumeAuthenticated,
    asTriggerOnly,
    onOpen,
    triggerRef
  } = t0;
  const {
    t
  } = useTranslation("priority");
  const {
    currentStatus
  } = useAuth("currentStatus");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getPublicConfig();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    NEXT_PUBLIC_BASE_URL_MYACCOUNT
  } = t1;
  let t2;
  if ($[1] !== onClose) {
    t2 = e => {
      const href = (e.target as HTMLElement).closest("a")?.getAttribute("href");
      const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
      if (href && href !== "#" && !isModifiedClick) {
        onClose?.();
      }
    };
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTakeoverNavigate = t2;
  const showLogIn = currentStatus !== AuthStatusEnum.Authenticated && !(assumeAuthenticated && currentStatus === AuthStatusEnum.InProgress);
  if (showLogIn) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ArkIcon iconName="user-01" size="20" />;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const t4 = urlLogin || NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#";
    let t5;
    if ($[4] !== t) {
      t5 = t("priority:header.myAccount");
      $[4] = t;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t) {
      t6 = t("priority:header.myAccount");
      $[6] = t;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t) {
      t7 = t("priority:header.logIn");
      $[8] = t;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
      t8 = <ButtonStyle size="md" variant="secondary" iconBefore={t3}><Link href={t4} className="NavAccount__logIn shrink-0" title={t5} aria-label={t6}>{t7}</Link></ButtonStyle>;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  const isLegacySkippySolution = !userMenu;
  if (isLegacySkippySolution) {
    let t3;
    if ($[15] !== t) {
      t3 = t("priority:header.myAccount");
      $[15] = t;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== t) {
      t4 = t("priority:header.myAccount");
      $[17] = t;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    let t5;
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AccountAvatar variant="minimal" size="md" className="hidden lg:flex" />;
      t6 = <AccountAvatar variant="dropdown" className="flex lg:hidden" />;
      $[19] = t5;
      $[20] = t6;
    } else {
      t5 = $[19];
      t6 = $[20];
    }
    let t7;
    if ($[21] !== t3 || $[22] !== t4) {
      t7 = <DropdownMenuTrigger asChild={true}><button type="button" className="NavAccount__avatar NavAccount__avatar--dropdown flex p-comfy-xs" title={t3} aria-label={t4}>{t5}{t6}</button></DropdownMenuTrigger>;
      $[21] = t3;
      $[22] = t4;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <DropdownMenuContent align="end" className="NavAccount__menu flex flex-col items-end mt-snug-lg py-snug-xl"><FallbackUserMenu /></DropdownMenuContent>;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] !== t7) {
      t9 = <DropdownMenu>{t7}{t8}</DropdownMenu>;
      $[25] = t7;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    return t9;
  }
  if (asTriggerOnly) {
    let t3;
    if ($[27] !== t) {
      t3 = t("priority:header.myAccount");
      $[27] = t;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    let t4;
    if ($[29] !== t) {
      t4 = t("priority:header.myAccount");
      $[29] = t;
      $[30] = t4;
    } else {
      t4 = $[30];
    }
    let t5;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AccountAvatar variant="dropdown" />;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    let t6;
    if ($[32] !== onOpen || $[33] !== t3 || $[34] !== t4) {
      t6 = <button type="button" onClick={onOpen} className="NavAccount__avatar NavAccount__avatar--dropdown flex items-center justify-center" title={t3} aria-label={t4}>{t5}</button>;
      $[32] = onOpen;
      $[33] = t3;
      $[34] = t4;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    return t6;
  }
  const t3 = urlAccountHome || NEXT_PUBLIC_BASE_URL_MYACCOUNT || "#";
  let t4;
  if ($[36] !== t) {
    t4 = t("priority:header.myAccount");
    $[36] = t;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  let t5;
  if ($[38] !== t) {
    t5 = t("priority:header.myAccount");
    $[38] = t;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AccountAvatar variant="minimal" size="md" />;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== t3 || $[42] !== t4 || $[43] !== t5) {
    t7 = <Link type="button" href={t3} className="NavAccount__avatar hidden lg:flex items-center justify-center no-underline" title={t4} aria-label={t5}>{t6}</Link>;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  let t8;
  if ($[45] !== t) {
    t8 = t("priority:header.myAccount");
    $[45] = t;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  let t9;
  if ($[47] !== t) {
    t9 = t("priority:header.myAccount");
    $[47] = t;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AccountAvatar variant="dropdown" />;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== t8 || $[51] !== t9 || $[52] !== triggerRef) {
    t11 = <NavigationMenu.Trigger ref={triggerRef} onPointerEnter={_temp} onPointerMove={_temp2} onPointerLeave={_temp3} className="NavAccount__avatar NavAccount__avatar--dropdown flex items-center justify-center" title={t8} aria-label={t9}>{t10}</NavigationMenu.Trigger>;
    $[50] = t8;
    $[51] = t9;
    $[52] = triggerRef;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== t) {
    t12 = t("priority:nav.close", {
      defaultValue: "Close"
    });
    $[54] = t;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ArkIcon iconName="x-close" className="NavAccount__icon" />;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== onClose || $[58] !== t12) {
    t14 = <div className="NavAccount__takeover-header flex items-center justify-end px-snug-md py-comfy-sm"><button type="button" className="NavAccount__takeover-close flex items-center p-comfy-xs" aria-label={t12} onClick={onClose}>{t13}</button></div>;
    $[57] = onClose;
    $[58] = t12;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== userMenu) {
    t15 = userMenu || <FallbackUserMenu itemWrapper="div" />;
    $[60] = userMenu;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== t15) {
    t16 = <div className="NavAccount__takeover-content px-comfy-sm pt-snug-xl">{t15}</div>;
    $[62] = t15;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t14 || $[65] !== t16) {
    t17 = <div className="NavAccount__takeover flex flex-col">{t14}{t16}</div>;
    $[64] = t14;
    $[65] = t16;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== handleTakeoverNavigate || $[68] !== t17) {
    t18 = <NavigationMenu.Content className="Header__content Header__content--mobile flex flex-col" onPointerLeave={_temp4} onClick={handleTakeoverNavigate}>{t17}</NavigationMenu.Content>;
    $[67] = handleTakeoverNavigate;
    $[68] = t17;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  let t19;
  if ($[70] !== t11 || $[71] !== t18) {
    t19 = <NavigationMenu.Item className="flex lg:hidden items-center" value="account" asChild={true}><div>{t11}{t18}</div></NavigationMenu.Item>;
    $[70] = t11;
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  let t20;
  if ($[73] !== t19 || $[74] !== t7) {
    t20 = <>{t7}{t19}</>;
    $[73] = t19;
    $[74] = t7;
    $[75] = t20;
  } else {
    t20 = $[75];
  }
  return t20;
};
function _temp(e_0) {
  return e_0.stopPropagation();
}
function _temp2(e_1) {
  return e_1.preventDefault();
}
function _temp3(e_2) {
  return e_2.preventDefault();
}
function _temp4(e_3) {
  return e_3.preventDefault();
}